// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { hasFeature } from '@tectonics/prs/feature';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useCurrentAllFeatures } from '@tectonics/prs/payment-spec';
import {
  useUserInputValidationError,
  type UserInputValidationError,
  serverErrorMessage,
} from '@tectonics/prs/validation';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useSubmitting } from '@tectonics/prs/submitting';
import type { Country } from '@tectonics/prs/country';

import { Field } from '../../../molecules';
import { Input, Empty, Label } from '../../../atoms';

export function FieldCardHolderName(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();
  const [value, handleChange] = useUserInputAsTextField('cardHolderName');
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('cardHolderName');
  const labelMessage = useLabelMessage();

  const allFeatures = useCurrentAllFeatures();
  const isHalfField = hasFeature(allFeatures, 'field_brazilian_document');

  return (
    <Field
      size={isHalfField ? 'half' : undefined}
      data-test-name="card-holder-name"
      label={<Label htmlFor={inputId}>{labelMessage}</Label>}
      showFloatingLabel={!!value}
      error={<ValidationErrorMessage validationError={validationError} />}
      hasError={validationError && formHasBeenSent}
    >
      <Input
        data-secret
        id={inputId}
        value={value}
        onChange={handleChange}
        readOnly={submitting}
        placeholder={labelMessage}
        name="card-holder-name"
      />
    </Field>
  );
}

function useLabelMessage(): string {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return 'Nome (como impresso no cartão)';
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'py':
      return 'Nombre del titular de la tarjeta';
    default:
      throw new Error(`No message found for field card holder name in country ${country}`);
  }
}

type ValidationErrorMessageProps = {|
  validationError: ?UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  if (!validationError) {
    return <Empty />;
  }

  const message = getErrorMessages(validationError)[country];

  if (message) {
    return message;
  }

  return serverErrorMessage(validationError.payload);
}

function getErrorMessages({ id }: UserInputValidationError): { [Country]: string } {
  switch (id) {
    case 'empty':
      return {
        br: 'Faltou uma informação aqui! Preencha com o nome que aparece em seu cartão.',
        mx: 'Completa tu nombre como aparece en tu tarjeta',
        ar: 'Completa tu nombre como aparece en tu tarjeta',
        co: 'Completa tu nombre como aparece en tu tarjeta',
        cl: 'Completa tu nombre como aparece en tu tarjeta',
        pe: 'Completa tu nombre como aparece en tu tarjeta',
        ec: 'Completa tu nombre como aparece en tu tarjeta',
        bo: 'Completa tu nombre como aparece en tu tarjeta',
      };

    case 'contains-number':
      return {
        br: 'Nome não pode conter números',
        mx: 'El nombre no puede contener números',
        ar: 'El nombre no puede contener números',
        co: 'El nombre no puede contener números',
        cl: 'El nombre no puede contener números',
        pe: 'El nombre no puede contener números',
        ec: 'El nombre no puede contener números',
        bo: 'El nombre no puede contener números',
      };

    case 'max-length':
      return {
        br: 'Nome não pode conter mais de 50 caracteres',
        mx: 'El nombre no puede contener más de 50 caracteres',
        ar: 'El nombre no puede contener más de 50 caracteres',
        co: 'El nombre no puede contener más de 50 caracteres',
        cl: 'El nombre no puede contener más de 50 caracteres',
        pe: 'El nombre no puede contener más de 50 caracteres',
        ec: 'El nombre no puede contener más de 50 caracteres',
        bo: 'El nombre no puede contener más de 50 caracteres',
      };

    default:
      return {
        br: 'Nome inválido',
        mx: 'nombre inválido',
        ar: 'nombre inválido',
        co: 'nombre inválido',
        cl: 'nombre inválido',
        pe: 'nombre inválido',
        ec: 'nombre inválido',
        bo: 'nombre inválido',
      };
  }
}
